import { getConceptStatusValue, getSubconceptStatusValue } from '../../concept/getConceptStatus'
import { Concept, ConceptsByCid, newConcept } from '../../concept/Concept'
import { testExport } from '../../persistence/exportInfo'
import _ from 'lodash'
import { isConceptStatus, isSuperconceptStatus } from '../../concept/ConceptTypeGuards'
import { CalcEngineState } from '../CalcEngine'
import { ConceptStatusValue, AnyConceptStatus, AnyConceptStatusesByCid, ConceptValueDataType, Export } from '@fusion-advantage/backcompat'
import { createSelector } from 'reselect'

export abstract class BaseRunContext {
	abstract getConcept(cid: string): Concept|null
	abstract getStatus(cid: string): AnyConceptStatus|null
	abstract getExport(): Export
	abstract getChangedInputCids(): Set<string>

	statusValue(cid: string): ConceptStatusValue {
		return getConceptStatusValue(this.getStatus(cid))
	}

	substatusValue(cid: string, subconceptCid: string): ConceptStatusValue {
		return getSubconceptStatusValue(this.getStatus(cid), subconceptCid)
	}

	hasAnySubstatusValue(cid: string, value: ConceptStatusValue): boolean {
		const status = this.getStatus(cid)
		if (isSuperconceptStatus(status)) {
			for (let subconceptCid in status.subconcepts) {
				if (this.substatusValue(cid, subconceptCid) === value) {
					return true
				}
			}
		}
		return false
	}

	hasAnyValue(cid: string): boolean {
		const status = this.getStatus(cid)
		if (isConceptStatus(status)) {
			if (status.value && status.value !== '') {
				return true
			}
		} else if (isSuperconceptStatus(status)) {
			for (let substatus of _.values(status.subconcepts)) {
				if (substatus.value && substatus.value !== '') {
					return true
				}
			}
		}
		return false
	}

	hasValidValueForDataType(cid: string, dataType: ConceptValueDataType): boolean {
		const status = this.getStatus(cid)
		if (!isConceptStatus(status)) return true
		const valStr = status.value?.toString() ?? ''

		switch(dataType) {
			case ConceptValueDataType.Integer:
				const integerRegex = /^-?\d+$/
    			return integerRegex.test(valStr);
			case ConceptValueDataType.Decimal:
				const numericRegex = /^-?\d+(\.\d+)?$/;
				return numericRegex.test(valStr);
			default:
				return true
		}
	}
}

interface StatusState {
	statuses: AnyConceptStatusesByCid
	prevStatuses: AnyConceptStatusesByCid
}

export class ReduxRunContext extends BaseRunContext {
	private export: Export
	private concepts: ConceptsByCid
	statuses: AnyConceptStatusesByCid
	prevStatuses: AnyConceptStatusesByCid

	constructor(state: CalcEngineState) {
		super()
		this.export = state.export
		this.concepts = state.concepts
		this.statuses = state.statuses
		this.prevStatuses = state.prevStatuses
	}

	getExport(): Export {
		return this.export
	}

	getConcept(cid: string): Concept|null {
		return this.concepts[cid] ?? null
	}

	getStatus(cid: string): AnyConceptStatus {
		return this.statuses[cid] ?? null
	}

	private selectGetChangedInputCids = createSelector(
		(state: StatusState) => state.statuses,
		(state: StatusState) => state.prevStatuses,
		(statuses, prevStatuses): Set<string> => new Set([
			// because this is redux, we can assume that statuses will not be modified
			// in place but completely replaced, so only a strict equality check is
			// required to know if a status update has occurred.
			...Object.keys(statuses).filter(cid => statuses[cid] !== prevStatuses[cid]),
			// technically statuses should not be deleted, but we'll check it anyway
			// since we wouldn't want to miss it if it did happen.
			...Object.keys(prevStatuses).filter(cid => statuses[cid] === undefined),
		])
	)

	getChangedInputCids(): Set<string> {
		return this.selectGetChangedInputCids(this)
	}
}

//
//
// SandboxRunContext
// TBD—  Find out if we really need this separate context for the calculation
// editor, or if we can clean that code up and use ReduxRunContext perhaps??
// In particular the getChangedInputCids() method seems a little dangerous,
// but I didn't dig into it too much.
//

export interface TestValues {
	[conceptCid: string]: {
		value: string | number
		subconcepts: {
			[subconceptCid: string]: string | number | boolean
		}
	}
}

export class SandboxRunContext extends BaseRunContext {
	private testValues: TestValues
	private concepts: { [key: string]: Concept }
	private export: Export = testExport

	constructor(testValues: TestValues, concepts: { [key: string]: Concept }) {
		super()
		this.testValues = testValues
		this.concepts = concepts
	}

	statusValue(cid: string): ConceptStatusValue {
		return this.testValues[cid]?.value ?? null
	}

	substatusValue(cid: string, subconceptCid: string): ConceptStatusValue {
		return this.testValues[cid]?.subconcepts[subconceptCid] ?? null
	}

	getConcept(cid: string): Concept {
		let concept = this.concepts[cid]
		if (!concept) {
			const testValues = this.testValues[cid]

			if (!testValues) {
				// NOTE that the interface allows returning null
				// if handled properly on the other side
				throw new Error(`[SandboxRunContext] concept ${cid} required but not found`)
			}

			concept = newConcept(cid, cid)
			concept.subconcepts = _.keys(testValues.subconcepts)
		}
		return concept
	}

	hasAnyValue(cid: string): boolean {
		const conceptData = this.testValues[cid]
		if (!conceptData) {
			return false
		}

		if (conceptData.value && conceptData.value !== '') {
			return true
		}

		for (const subconceptValue of _.values(conceptData.subconcepts)) {
			if (subconceptValue && subconceptValue !== '') {
				return true
			}
		}

		return false
	}

	hasAnySubstatusValue(cid: string, value: ConceptStatusValue): boolean {
		const conceptData = this.testValues[cid]
		if (!conceptData) {
			return false
		}

		for (const subconceptValue of _.values(conceptData.subconcepts)) {
			if (subconceptValue === value) {
				return true
			}
		}

		return false
	}

	getStatus(cid: string): AnyConceptStatus {
		throw new Error('Method not implemented.')
	}

	getExport(): Export {
		return this.export
	}

	getChangedInputCids(): Set<string> {
		// Return all keys as changed in sandbox environment
		return new Set(_.keys(this.testValues))
	}
}